/**
 * Execution Analytics Page
 *
 * Detailed execution metrics and trends analysis.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnalyticsSubNav } from '../components/layout/AnalyticsLayout';
import { useIsPro } from '../hooks/useSubscription';
import { ProUpgradeCard } from '../components/billing/ProUpgradeCard';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { LoadingSkeleton } from '../components/common/LoadingSkeleton';
import {
  useExecutionAnalytics,
  formatDuration,
  formatPercentage,
  formatNumber,
} from '../hooks/useAnalytics';
import { TimeRange, AgentMetrics } from '../types/analytics';

// Chart colors
const CHART_COLORS = {
  COMPLETED: '#22c55e',
  FAILED: '#ef4444',
  RUNNING: '#3b82f6',
  PENDING: '#94a3b8',
  CANCELLED: '#f59e0b',
};

const STATUS_COLORS = ['#22c55e', '#ef4444', '#3b82f6', '#94a3b8', '#f59e0b'];

/**
 * Time Range Selector
 */
function TimeRangeSelector({
  value,
  onChange,
}: {
  value: TimeRange;
  onChange: (range: TimeRange) => void;
}) {
  const options: { value: TimeRange; label: string }[] = [
    { value: '7d', label: '7 Days' },
    { value: '30d', label: '30 Days' },
    { value: '90d', label: '90 Days' },
  ];

  return (
    <div className="flex items-center gap-2">
      {options.map((option) => (
        <Button
          key={option.value}
          variant={value === option.value ? 'default' : 'outline'}
          size="sm"
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </Button>
      ))}
    </div>
  );
}

/**
 * Metric Card Component
 */
function MetricCard({
  title,
  value,
  subtitle,
  icon,
  isLoading,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: string;
  isLoading?: boolean;
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        {isLoading ? (
          <>
            <LoadingSkeleton className="h-4 w-24 mb-2" />
            <LoadingSkeleton className="h-8 w-32" />
          </>
        ) : (
          <>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted-foreground">{title}</span>
              <span className="text-2xl">{icon}</span>
            </div>
            <div className="text-2xl font-bold">{value}</div>
            {subtitle && (
              <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Agent Performance Table
 */
function AgentPerformanceTable({
  agents,
  isLoading,
}: {
  agents: AgentMetrics[];
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <LoadingSkeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (agents.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No agent data available
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b text-left text-sm text-muted-foreground">
            <th className="pb-3 font-medium">Agent</th>
            <th className="pb-3 font-medium">Type</th>
            <th className="pb-3 font-medium text-right">Executions</th>
            <th className="pb-3 font-medium text-right">Success Rate</th>
            <th className="pb-3 font-medium text-right">Avg Duration</th>
          </tr>
        </thead>
        <tbody>
          {agents.map((agent) => (
            <tr key={agent.agentId} className="border-b last:border-0">
              <td className="py-3">
                <Link
                  to={`/agents/${agent.agentId}`}
                  className="font-medium hover:text-primary"
                >
                  {agent.agentName}
                </Link>
              </td>
              <td className="py-3 text-sm text-muted-foreground">
                {agent.agentType}
              </td>
              <td className="py-3 text-sm text-right">
                {formatNumber(agent.totalExecutions)}
              </td>
              <td className="py-3 text-right">
                <span
                  className={`text-sm font-medium ${
                    agent.successRate >= 90
                      ? 'text-green-600'
                      : agent.successRate >= 70
                      ? 'text-yellow-600'
                      : 'text-red-600'
                  }`}
                >
                  {formatPercentage(agent.successRate)}
                </span>
              </td>
              <td className="py-3 text-sm text-right">
                {formatDuration(agent.averageDuration)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Execution Analytics Page Component
 */
export default function ExecutionAnalytics() {
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');
  const { isPro, isLoading: planLoading } = useIsPro();

  const { data, isLoading, error } = useExecutionAnalytics({ timeRange }, isPro);

  if (!planLoading && !isPro) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <div className="max-w-7xl mx-auto py-8">
          <AnalyticsSubNav />
          <ProUpgradeCard
            feature="Execution analytics"
            description="Success rates, durations and per-agent performance. Upgrade to Pro ($29/mo) to unlock advanced analytics."
          />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <div className="max-w-7xl mx-auto py-8">
          <Card>
            <CardContent className="py-12 text-center">
              <div className="text-6xl mb-4">❌</div>
              <CardTitle className="mb-2">Error Loading Analytics</CardTitle>
              <CardDescription>
                {error instanceof Error ? error.message : 'An error occurred'}
              </CardDescription>
              <Button
                className="mt-4"
                variant="outline"
                onClick={() => window.location.reload()}
              >
                Retry
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const metrics = data?.metrics;
  const trend = data?.trend;
  const byAgent = data?.byAgent || [];
  const byStatus = data?.byStatus || [];
  const byHour = data?.byHour || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="max-w-7xl mx-auto py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold">Execution Analytics</h1>
            <p className="text-muted-foreground">
              Detailed execution metrics and performance trends
            </p>
          </div>
          <div className="flex items-center gap-4">
            <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
            <AnalyticsSubNav />
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <MetricCard
            title="Total Executions"
            value={metrics ? formatNumber(metrics.totalRuns) : '-'}
            icon="🚀"
            isLoading={isLoading}
          />
          <MetricCard
            title="Success Rate"
            value={metrics ? formatPercentage(metrics.successRate) : '-'}
            subtitle={metrics ? `${metrics.successfulRuns} successful` : undefined}
            icon="✅"
            isLoading={isLoading}
          />
          <MetricCard
            title="Failed"
            value={metrics ? formatNumber(metrics.failedRuns) : '-'}
            subtitle={
              metrics
                ? formatPercentage(100 - metrics.successRate) + ' failure rate'
                : undefined
            }
            icon="❌"
            isLoading={isLoading}
          />
          <MetricCard
            title="Avg Duration"
            value={metrics ? formatDuration(metrics.averageDuration) : '-'}
            subtitle={
              metrics
                ? `Max: ${formatDuration(metrics.maxDuration)}`
                : undefined
            }
            icon="⏱️"
            isLoading={isLoading}
          />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Execution Trend Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Execution Trend</CardTitle>
              <CardDescription>Daily execution counts over time</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <LoadingSkeleton className="h-64 w-full" />
              ) : trend?.data && trend.data.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={trend.data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) =>
                        new Date(value).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                        })
                      }
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip
                      labelFormatter={(value) =>
                        new Date(value).toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                        })
                      }
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="successful"
                      name="Successful"
                      stroke={CHART_COLORS.COMPLETED}
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="failed"
                      name="Failed"
                      stroke={CHART_COLORS.FAILED}
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  No data available
                </div>
              )}
            </CardContent>
          </Card>

          {/* Status Distribution */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Status Distribution</CardTitle>
              <CardDescription>Execution outcomes breakdown</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <LoadingSkeleton className="h-64 w-full" />
              ) : byStatus.length > 0 ? (
                <div className="flex items-center">
                  <ResponsiveContainer width="60%" height={280}>
                    <PieChart>
                      <Pie
                        data={byStatus}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="count"
                        nameKey="status"
                      >
                        {byStatus.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={
                              CHART_COLORS[entry.status as keyof typeof CHART_COLORS] ||
                              STATUS_COLORS[index % STATUS_COLORS.length]
                            }
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="w-40 space-y-2">
                    {byStatus.map((entry, index) => (
                      <div key={entry.status} className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{
                            backgroundColor:
                              CHART_COLORS[entry.status as keyof typeof CHART_COLORS] ||
                              STATUS_COLORS[index % STATUS_COLORS.length],
                          }}
                        />
                        <div className="flex-1">
                          <p className="text-xs font-medium">{entry.status}</p>
                          <p className="text-xs text-muted-foreground">
                            {entry.count} ({formatPercentage(entry.percentage)})
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  No data available
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Hourly Distribution Chart */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Hourly Distribution</CardTitle>
            <CardDescription>Executions by hour of day</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <LoadingSkeleton className="h-48 w-full" />
            ) : byHour.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={byHour}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="hour"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => `${value}:00`}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    labelFormatter={(value) => `${value}:00 - ${value}:59`}
                  />
                  <Bar dataKey="count" name="Executions" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-48 flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Agent Performance Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Agent Performance</CardTitle>
            <CardDescription>Execution metrics by agent</CardDescription>
          </CardHeader>
          <CardContent>
            <AgentPerformanceTable agents={byAgent} isLoading={isLoading} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
